/** Resolved entirely by the backend and sent on /auth/me; the client never derives it from a country code, so tables cannot drift. */
export interface Capabilities {
  kyc: boolean
  payouts: boolean
  /** Tenants can pay rent online. Recording a payment offline is never gated. */
  payments: boolean
  /** Which payout-account form to render — `ifsc`, `iban`, `routing`. Empty means the backend does
   *  not know the country, which means India's form: the only shape any stored account uses. */
  bankAccountShape: string
  agreements: boolean
  eSign: boolean
  eStamp: boolean
}

/** The wire block, every key optional — an older backend sends none of it. */
export interface RawCapabilities {
  kyc?: unknown
  payouts?: unknown
  payments?: unknown
  bankAccountShape?: unknown
  agreements?: unknown
  eSign?: unknown
  eStamp?: unknown
}

/** Everything on: before boot completes, and when the backend sends no `capabilities` at all. */
export const ALLOWED: Capabilities = { kyc: true, payouts: true, payments: true, bankAccountShape: '', agreements: true, eSign: true, eStamp: true }

// A missing `capabilities` object means the backend predates this: fall back to ALLOWED, or a frontend release hides KYC/payouts/payment.
// A missing key inside it means the client predates that capability: reads false, so the backend can add one without a deploy here.
// Absence of information never removes something a person has; a deliberate answer is obeyed.
export function readCapabilities(raw: unknown): Capabilities {
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) return ALLOWED
  const block = raw as RawCapabilities
  return {
    // `=== true` and not a truthy check: a backend sending a string or a number must not read as on.
    kyc: block.kyc === true,
    payouts: block.payouts === true,
    payments: block.payments === true,
    bankAccountShape: typeof block.bankAccountShape === 'string' ? block.bankAccountShape : '',
    agreements: block.agreements === true,
    eSign: block.eSign === true,
    eStamp: block.eStamp === true,
  }
}

/** Value equality, so the store can tell "the answer changed" from "a new object arrived". */
export function sameCapabilities(a: Capabilities, b: Capabilities): boolean {
  return (
    a.kyc === b.kyc &&
    a.payouts === b.payouts &&
    a.payments === b.payments &&
    a.bankAccountShape === b.bankAccountShape &&
    a.agreements === b.agreements &&
    a.eSign === b.eSign &&
    a.eStamp === b.eStamp
  )
}

// Property/lease capabilities differ from the person's: agreements follow the building's country, payouts need both countries.
// A record with no block means an older backend and reads as everything allowed.
export function readRecordCapabilities(record: { capabilities?: RawCapabilities } | undefined | null): Capabilities {
  return readCapabilities(record?.capabilities)
}
